/**
 * Shieldz key generation — single BIP-39 mnemonic → all four chain
 * primitives (BTC zpub, EVM address, Zcash UFVK) derived in-browser.
 *
 * Shieldz never sees the mnemonic. Only the public derivation results
 * (xpub, address, ufvk) leave this module — and even then only to the
 * merchant's own `merchant_chain_configs` POST.
 *
 * Derivation paths (matched to backend ChainDriver expectations):
 *   - BTC : BIP-84 `m/84'/0'/0'`, account xpub re-encoded as `zpub…`
 *   - EVM : BIP-44 `m/44'/60'/0'/0/0`, first EOA address (same on
 *           BASE / ARB / OP / POLY / ETH)
 *   - ZEC : ZIP-32 account 0, encoded as a `uview1…` UFVK via the
 *           leokit-shielded-sign WASM crate's `derive_ufvk_from_seed`
 *
 * Encrypted-keystore format: AES-GCM with a PBKDF2(SHA-256, 600 000
 * iters) key, salt and iv random per export.
 */

import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";
import { HDKey } from "@scure/bip32";
import { base58, base58check, bech32 } from "@scure/base";
import { sha256, sha512 } from "@noble/hashes/sha2.js";
import { ripemd160 } from "@noble/hashes/legacy.js";
import { hmac } from "@noble/hashes/hmac.js";
import { keccak_256 } from "@noble/hashes/sha3.js";
import { scrypt } from "@noble/hashes/scrypt.js";
import { secp256k1 } from "@noble/curves/secp256k1.js";
import { ed25519 } from "@noble/curves/ed25519.js";
import { mnemonicToAccount, privateKeyToAccount } from "viem/accounts";
import { pbkdf2 } from "@noble/hashes/pbkdf2.js";
import type { LocalAccount } from "viem";

// ─── Mnemonic ────────────────────────────────────────────────────────

/** Generate a fresh 12-word BIP-39 English mnemonic (128 bits of entropy). */
export const generateShieldzMnemonic = (): string => generateMnemonic(wordlist, 128);

/** True iff `words` is a valid BIP-39 English mnemonic. */
export const isValidMnemonic = (words: string): boolean =>
  validateMnemonic(words.trim().toLowerCase().split(/\s+/).join(" "), wordlist);

// ─── BTC: BIP-84 zpub ────────────────────────────────────────────────

// SLIP-132 version bytes for mainnet BIP-84 extended public key (zpub).
const ZPUB_VERSION = new Uint8Array([0x04, 0xb2, 0x47, 0x46]);

// base58check needs a sync sha256; we wire in @noble/hashes' implementation.
const b58c = base58check(sha256);

/**
 * Derive the merchant's BIP-84 account zpub for Bitcoin. The backend
 * derives per-invoice receive addresses by walking `m/0/i` against this
 * zpub via @scure/bip32.
 */
export const deriveBtcZpub = (mnemonic: string): string => {
  const seed = mnemonicToSeedSync(mnemonic);
  const master = HDKey.fromMasterSeed(seed);
  const account = master.derive("m/84'/0'/0'");
  // publicExtendedKey is the standard xpub form. We swap the 4-byte
  // version prefix in place to convert to the SLIP-132 zpub form
  // without touching the key bytes.
  const decoded = b58c.decode(account.publicExtendedKey);
  const reframed = new Uint8Array(decoded.length);
  reframed.set(ZPUB_VERSION, 0);
  reframed.set(decoded.subarray(4), 4);
  return b58c.encode(reframed);
};

// ─── EVM: BIP-44 first address ───────────────────────────────────────

/**
 * Derive the merchant's first EOA address. viem's `mnemonicToAccount`
 * defaults to `m/44'/60'/0'/0/0`, the standard MetaMask path. The same
 * address works on every EVM chain we support.
 */
export const deriveEvmAddress = (mnemonic: string): string => {
  const account = mnemonicToAccount(mnemonic);
  return account.address;
};

// ─── Tron: BIP-44 m/44'/195'/0'/0/0 (secp256k1) ──────────────────────

/**
 * Derive the merchant's Tron address — the swap-refund target for
 * USDT-on-Tron. Same secp256k1 key family as EVM, just a different
 * address encoding: keccak256(uncompressed pubkey)[-20:], prefixed with
 * Tron's 0x41 mainnet byte and base58check-encoded (double-SHA256
 * checksum). This is the standard path TronLink/Ledger use, so the
 * merchant recovers the same address by importing their seed there.
 * Verified against viem's secp256k1→keccak address derivation.
 */
export const deriveTronAddress = (mnemonic: string): string => {
  const seed = mnemonicToSeedSync(mnemonic);
  const node = HDKey.fromMasterSeed(seed).derive("m/44'/195'/0'/0/0");
  if (!node.privateKey) throw new Error("Tron derivation produced no key");
  const pub = secp256k1.getPublicKey(node.privateKey, false); // 65B: 0x04||X||Y
  const hash20 = keccak_256(pub.subarray(1)).subarray(12); // last 20 bytes
  const payload = new Uint8Array(21);
  payload[0] = 0x41; // Tron mainnet address prefix
  payload.set(hash20, 1);
  return b58c.encode(payload);
};

// ─── Solana: SLIP-0010 m/44'/501'/0'/0' (ed25519) ────────────────────

// ed25519 uses SLIP-0010 (not BIP-32) — all path levels are hardened.
const ED25519_SLIP10_KEY = new TextEncoder().encode("ed25519 seed");

const slip10MasterEd = (seed: Uint8Array): { key: Uint8Array; chainCode: Uint8Array } => {
  const I = hmac(sha512, ED25519_SLIP10_KEY, seed);
  return { key: I.slice(0, 32), chainCode: I.slice(32) };
};

const slip10CkdEd = (
  parent: { key: Uint8Array; chainCode: Uint8Array },
  hardenedIndex: number,
): { key: Uint8Array; chainCode: Uint8Array } => {
  // data = 0x00 || ser256(key) || ser32(index); index is always hardened.
  const data = new Uint8Array(37);
  data[0] = 0x00;
  data.set(parent.key, 1);
  new DataView(data.buffer).setUint32(33, (hardenedIndex | 0x80000000) >>> 0, false);
  const I = hmac(sha512, parent.chainCode, data);
  return { key: I.slice(0, 32), chainCode: I.slice(32) };
};

/**
 * Derive the merchant's Solana address — the swap-refund target for
 * USDC/USDT-on-Solana. Solana is ed25519, so it uses SLIP-0010 over the
 * BIP-39 seed at m/44'/501'/0'/0' (Phantom's default path). The address
 * is the base58 of the 32-byte ed25519 public key. Verified against the
 * official SLIP-0010 ed25519 test vectors; the merchant recovers the same
 * address by importing their seed into Phantom.
 */
export const deriveSolanaAddress = (mnemonic: string): string => {
  const seed = mnemonicToSeedSync(mnemonic);
  let node = slip10MasterEd(seed);
  for (const idx of [44, 501, 0, 0]) node = slip10CkdEd(node, idx);
  return base58.encode(ed25519.getPublicKey(node.key));
};

// ─── NEAR: SLIP-0010 m/44'/397'/0' (ed25519) ─────────────────────────

const toHex = (b: Uint8Array): string =>
  Array.from(b)
    .map((x) => x.toString(16).padStart(2, "0"))
    .join("");

const fromHex = (s: string): Uint8Array => {
  const h = s.startsWith("0x") || s.startsWith("0X") ? s.slice(2) : s;
  if (h.length % 2 !== 0) throw new Error("invalid hex");
  const out = new Uint8Array(h.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = Number.parseInt(h.slice(i * 2, i * 2 + 2), 16);
  return out;
};

/**
 * Derive the merchant's NEAR implicit account — the swap-refund target for
 * NEAR. NEAR is ed25519; its implicit account ID is the lowercase hex of the
 * 32-byte public key derived via SLIP-0010 at m/44'/397'/0' (the NEAR CLI /
 * Ledger path). Recoverable by importing the seed into a NEAR wallet.
 */
export const deriveNearAccount = (mnemonic: string): string => {
  const seed = mnemonicToSeedSync(mnemonic);
  let node = slip10MasterEd(seed);
  for (const idx of [44, 397, 0]) node = slip10CkdEd(node, idx);
  return toHex(ed25519.getPublicKey(node.key));
};

// ─── Litecoin / Dash: single refund address (secp256k1) ──────────────

const hash160 = (pub: Uint8Array): Uint8Array => ripemd160(sha256(pub));

/**
 * Litecoin refund address (BIP-84 m/84'/2'/0'/0/0 → ltc1… bech32 P2WPKH).
 * Matches the backend's index-0 derivation exactly, so a failed-swap refund
 * lands at an address the merchant recovers from the same seed.
 */
export const deriveLtcAddress = (mnemonic: string): string => {
  const node = HDKey.fromMasterSeed(mnemonicToSeedSync(mnemonic)).derive("m/84'/2'/0'/0/0");
  if (!node.publicKey) throw new Error("LTC derivation produced no key");
  return bech32.encode("ltc", [0, ...bech32.toWords(hash160(node.publicKey))]);
};

/** Dash refund address (BIP-44 m/44'/5'/0'/0/0 → X… base58check, version 0x4c). */
export const deriveDashAddress = (mnemonic: string): string => {
  const node = HDKey.fromMasterSeed(mnemonicToSeedSync(mnemonic)).derive("m/44'/5'/0'/0/0");
  if (!node.publicKey) throw new Error("DASH derivation produced no key");
  const payload = new Uint8Array(21);
  payload[0] = 0x4c;
  payload.set(hash160(node.publicKey), 1);
  return b58c.encode(payload);
};

/**
 * Zcash TRANSPARENT refund address (BIP-44 m/44'/133'/0'/0/0 → t1… P2PKH,
 * 2-byte mainnet version 0x1CB8 + hash160, base58check). Distinct from the
 * merchant's shielded ZEC receive (UFVK) — this is the swap-in refund target.
 */
export const deriveZecTransparentAddress = (mnemonic: string): string => {
  const node = HDKey.fromMasterSeed(mnemonicToSeedSync(mnemonic)).derive("m/44'/133'/0'/0/0");
  if (!node.publicKey) throw new Error("ZEC derivation produced no key");
  const payload = new Uint8Array(2 + 20);
  payload[0] = 0x1c;
  payload[1] = 0xb8;
  payload.set(hash160(node.publicKey), 2);
  return b58c.encode(payload);
};

// ─── Bitcoin Cash: cashaddr (secp256k1) ──────────────────────────────
const CASHADDR_CHARSET = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
const cashaddrPolymod = (values: number[]): bigint => {
  let c = 1n;
  for (const d of values) {
    const c0 = c >> 35n;
    c = ((c & 0x07ffffffffn) << 5n) ^ BigInt(d);
    if (c0 & 0x01n) c ^= 0x98f2bc8e61n;
    if (c0 & 0x02n) c ^= 0x79b76d99e2n;
    if (c0 & 0x04n) c ^= 0xf33e5fb3c4n;
    if (c0 & 0x08n) c ^= 0xae2eabe2a8n;
    if (c0 & 0x10n) c ^= 0x1e4f43e470n;
  }
  return c ^ 1n;
};
const to5Bit = (data: Uint8Array): number[] => {
  let acc = 0;
  let bits = 0;
  const out: number[] = [];
  for (const b of data) {
    acc = (acc << 8) | b;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      out.push((acc >> bits) & 31);
    }
  }
  if (bits) out.push((acc << (5 - bits)) & 31);
  return out;
};

/**
 * Bitcoin Cash refund address (BIP-44 m/44'/145'/0'/0/0 → cashaddr P2PKH).
 * Version byte 0 (P2PKH / 160-bit), 40-bit BCH polymod checksum. Verified
 * against the cashaddr spec test vectors.
 */
export const deriveBchAddress = (mnemonic: string): string => {
  const node = HDKey.fromMasterSeed(mnemonicToSeedSync(mnemonic)).derive("m/44'/145'/0'/0/0");
  if (!node.publicKey) throw new Error("BCH derivation produced no key");
  const prefix = "bitcoincash";
  const payload = to5Bit(new Uint8Array([0, ...hash160(node.publicKey)]));
  const pre = [...prefix].map((c) => c.charCodeAt(0) & 31);
  const cs = cashaddrPolymod([...pre, 0, ...payload, 0, 0, 0, 0, 0, 0, 0, 0]);
  const csb: number[] = [];
  for (let i = 0; i < 8; i++) csb.push(Number((cs >> BigInt(5 * (7 - i))) & 31n));
  return `${prefix}:${[...payload, ...csb].map((x) => CASHADDR_CHARSET[x]).join("")}`;
};

/** Every optional-coin refund address derived from one seed, plus the EVM
 *  address (used to verify the keystore belongs to this account). */
export interface DerivedRefunds {
  evm: string;
  tron: string;
  solana: string;
  near: string;
  ltc: string;
  dash: string;
  zec: string;
  bch: string;
}

export const deriveAllRefunds = (mnemonic: string): DerivedRefunds => ({
  evm: deriveEvmAddress(mnemonic),
  tron: deriveTronAddress(mnemonic),
  solana: deriveSolanaAddress(mnemonic),
  near: deriveNearAccount(mnemonic),
  ltc: deriveLtcAddress(mnemonic),
  dash: deriveDashAddress(mnemonic),
  zec: deriveZecTransparentAddress(mnemonic),
  bch: deriveBchAddress(mnemonic),
});

// ─── Standard Web3 V3 keystore (works in MetaMask / Geth / ethers) ───

/** EVM account private key (m/44'/60'/0'/0/0) — same account as
 *  deriveEvmAddress. 32 raw bytes. Never leaves the browser. */
const evmPrivateKey = (mnemonic: string): Uint8Array => {
  const node = HDKey.fromMasterSeed(mnemonicToSeedSync(mnemonic)).derive("m/44'/60'/0'/0/0");
  if (!node.privateKey) throw new Error("could not derive EVM private key");
  return node.privateKey;
};

/**
 * Encrypt the merchant's EVM key as a STANDARD Web3 Secret Storage (V3)
 * keystore — the JSON MetaMask/Geth/ethers import directly ("Import Account
 * → JSON File"). Unlike our app keystore (which wraps the whole mnemonic),
 * this is the universally-recognized single-key format. scrypt KDF +
 * AES-128-CTR + keccak MAC, per the spec.
 */
export const exportV3Keystore = async (
  mnemonic: string,
  password: string,
): Promise<object> => {
  if (password.length < 8) throw new Error("Password must be at least 8 characters.");
  const priv = evmPrivateKey(mnemonic);
  const address = deriveEvmAddress(mnemonic).slice(2).toLowerCase();
  const salt = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const n = 131072;
  const r = 8;
  const p = 1;
  const dklen = 32;
  const dk = scrypt(new TextEncoder().encode(password), salt, { N: n, r, p, dkLen: dklen });
  const encKey = dk.slice(0, 16);
  const cryptoKey = await crypto.subtle.importKey(
    "raw", encKey as BufferSource, { name: "AES-CTR" }, false, ["encrypt"],
  );
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-CTR", counter: iv as BufferSource, length: 128 },
      cryptoKey,
      priv as BufferSource,
    ),
  );
  const mac = keccak_256(new Uint8Array([...dk.slice(16, 32), ...ct]));
  return {
    version: 3,
    id: crypto.randomUUID(),
    address,
    crypto: {
      ciphertext: toHex(ct),
      cipherparams: { iv: toHex(iv) },
      cipher: "aes-128-ctr",
      kdf: "scrypt",
      kdfparams: { dklen, salt: toHex(salt), n, r, p },
      mac: toHex(mac),
    },
  };
};

/** Trigger a browser download of any JSON object. */
export const downloadJson = (obj: object, filename: string): void => {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ─── ZEC: UFVK via WASM ─────────────────────────────────────────────

/**
 * Load and call the leokit-shielded-sign WASM crate to derive the
 * merchant's Unified Full Viewing Key. The WASM is loaded lazily so
 * the rest of the wizard stays snappy.
 */
export const deriveZecUfvk = async (mnemonic: string): Promise<string> => {
  const wasm = await import("leokit-shielded-sign");
  await wasm.default();
  return wasm.derive_ufvk_from_seed(mnemonic, 0);
};

/** All four primitives derived at once. UFVK is async (WASM). */
export interface DerivedPrimitives {
  btc_zpub: string;
  evm_address: string;
  zec_ufvk: string;
}

export const deriveAll = async (mnemonic: string): Promise<DerivedPrimitives> => {
  const [btc_zpub, evm_address, zec_ufvk] = await Promise.all([
    Promise.resolve(deriveBtcZpub(mnemonic)),
    Promise.resolve(deriveEvmAddress(mnemonic)),
    deriveZecUfvk(mnemonic),
  ]);
  return { btc_zpub, evm_address, zec_ufvk };
};

// ─── Encrypted keystore ─────────────────────────────────────────────

const KEYSTORE_VERSION = 1;
const PBKDF2_ITERATIONS = 600_000;
const KEY_LEN_BITS = 256;

export interface Keystore {
  version: number;
  kdf: "PBKDF2-SHA256";
  iterations: number;
  salt_b64: string;
  iv_b64: string;
  ct_b64: string;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

const toB64 = (b: Uint8Array): string => btoa(String.fromCharCode(...b));
const fromB64 = (s: string): Uint8Array =>
  Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

const deriveKey = async (
  passphrase: string,
  salt: Uint8Array,
): Promise<CryptoKey> => {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(passphrase) as BufferSource,
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: KEY_LEN_BITS },
    false,
    ["encrypt", "decrypt"],
  );
};

/** Encrypt a mnemonic with a passphrase. Returns a JSON-shaped keystore. */
export const exportKeystore = async (
  mnemonic: string,
  passphrase: string,
): Promise<Keystore> => {
  if (passphrase.length < 8) {
    throw new Error("Passphrase must be at least 8 characters.");
  }
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: iv as BufferSource },
      key,
      enc.encode(mnemonic) as BufferSource,
    ),
  );
  return {
    version: KEYSTORE_VERSION,
    kdf: "PBKDF2-SHA256",
    iterations: PBKDF2_ITERATIONS,
    salt_b64: toB64(salt),
    iv_b64: toB64(iv),
    ct_b64: toB64(ct),
  };
};

/** Decrypt a keystore back to the mnemonic. Throws on wrong passphrase. */
export const importKeystore = async (
  blob: Keystore,
  passphrase: string,
): Promise<string> => {
  if (blob.version !== KEYSTORE_VERSION) {
    throw new Error(`unsupported keystore version: ${blob.version}`);
  }
  const salt = fromB64(blob.salt_b64);
  const iv = fromB64(blob.iv_b64);
  const ct = fromB64(blob.ct_b64);
  const key = await deriveKey(passphrase, salt);
  const pt = new Uint8Array(
    await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: iv as BufferSource },
      key,
      ct as BufferSource,
    ),
  );
  return dec.decode(pt);
};

// ─── Login: decrypt ANY supported secret into a signing account ─────────
//
// The dashboard's web login is Sign-In-With-Ethereum: the server issues a
// nonce, the client signs it, the server recovers the signer address =
// the account identity. A keystore login is the same flow with an in-browser
// signer derived from a file/phrase instead of an injected wallet. The signing
// key is the EVM account at m/44'/60'/0'/0/0 — identical to deriveEvmAddress —
// so a keystore login lands on the SAME account as "Connect Wallet" with that
// key. The secret never leaves the browser; only the SIWE signature is sent.

/** Standard Web3 (V3) Secret-Storage keystore — the MetaMask/Geth/ethers
 *  format. Decrypts to the raw 32-byte EVM private key. Supports both scrypt
 *  and pbkdf2 KDFs and is tolerant of the `crypto`/`Crypto` casing variants
 *  seen across wallets. Throws on a wrong password (MAC mismatch). */
export const importV3Keystore = async (
  blob: Record<string, unknown>,
  password: string,
): Promise<Uint8Array> => {
  const cryptoSection = (blob.crypto ?? blob.Crypto) as Record<string, unknown> | undefined;
  if (!cryptoSection) throw new Error("Not a V3 keystore (missing crypto section).");
  const cipher = String(cryptoSection.cipher ?? "");
  if (cipher !== "aes-128-ctr") throw new Error(`Unsupported cipher: ${cipher || "none"}`);
  const kdf = String(cryptoSection.kdf ?? "");
  const kdfparams = (cryptoSection.kdfparams ?? {}) as Record<string, unknown>;
  const salt = fromHex(String(kdfparams.salt ?? ""));
  const dklen = Number(kdfparams.dklen ?? 32);
  const pw = enc.encode(password);

  let dk: Uint8Array;
  if (kdf === "scrypt") {
    dk = scrypt(pw, salt, {
      N: Number(kdfparams.n),
      r: Number(kdfparams.r),
      p: Number(kdfparams.p),
      dkLen: dklen,
    });
  } else if (kdf === "pbkdf2") {
    const prf = String(kdfparams.prf ?? "hmac-sha256");
    if (prf !== "hmac-sha256") throw new Error(`Unsupported pbkdf2 prf: ${prf}`);
    dk = pbkdf2(sha256, pw, salt, { c: Number(kdfparams.c), dkLen: dklen });
  } else {
    throw new Error(`Unsupported KDF: ${kdf || "none"}`);
  }

  const ct = fromHex(String(cryptoSection.ciphertext ?? ""));
  // MAC = keccak256(dk[16:32] ++ ciphertext) — verifies the password before
  // we trust the decrypted bytes (constant work; cheap relative to the KDF).
  const mac = toHex(keccak_256(new Uint8Array([...dk.slice(16, 32), ...ct])));
  const expected = String(cryptoSection.mac ?? "").toLowerCase().replace(/^0x/, "");
  if (mac !== expected) throw new Error("Wrong password or corrupt keystore.");

  const cipherparams = (cryptoSection.cipherparams ?? {}) as Record<string, unknown>;
  const iv = fromHex(String(cipherparams.iv ?? ""));
  const aesKey = await crypto.subtle.importKey(
    "raw",
    dk.slice(0, 16) as BufferSource,
    { name: "AES-CTR" },
    false,
    ["decrypt"],
  );
  const priv = new Uint8Array(
    await crypto.subtle.decrypt(
      { name: "AES-CTR", counter: iv as BufferSource, length: 128 },
      aesKey,
      ct as BufferSource,
    ),
  );
  if (priv.length !== 32) throw new Error("Decrypted key is not 32 bytes.");
  return priv;
};

/**
 * XChainJS keystore (THORSwap / ASGARDEX / XDEFI / THORWallet) — version 1,
 * meta "xchain-keystore". Unlike a Web3 V3 file (which holds a raw private key
 * and a keccak MAC), XChainJS encrypts the BIP-39 PHRASE with pbkdf2 +
 * aes-128-ctr and authenticates with a BLAKE-family MAC. @noble/hashes doesn't
 * ship that BLAKE variant, so instead of verifying the MAC we decrypt and
 * require the result to be a valid BIP-39 mnemonic — a stronger correctness
 * check for our purposes, and it cleanly rejects a wrong password (garbage
 * plaintext → invalid mnemonic). Returns the decrypted phrase.
 */
export const importXchainKeystore = async (
  blob: Record<string, unknown>,
  password: string,
): Promise<string> => {
  const c = (blob.crypto ?? blob.Crypto) as Record<string, unknown> | undefined;
  if (!c) throw new Error("Not an XChainJS keystore (missing crypto section).");
  const cipher = String(c.cipher ?? "");
  if (cipher !== "aes-128-ctr") throw new Error(`Unsupported cipher: ${cipher || "none"}`);
  const kdf = String(c.kdf ?? "");
  const kp = (c.kdfparams ?? {}) as Record<string, unknown>;
  const salt = fromHex(String(kp.salt ?? ""));
  const dklen = Number(kp.dklen ?? 32);
  const pw = enc.encode(password);

  let dk: Uint8Array;
  if (kdf === "pbkdf2") {
    const prf = String(kp.prf ?? "hmac-sha256");
    if (prf !== "hmac-sha256") throw new Error(`Unsupported pbkdf2 prf: ${prf}`);
    dk = pbkdf2(sha256, pw, salt, { c: Number(kp.c), dkLen: dklen });
  } else if (kdf === "scrypt") {
    dk = scrypt(pw, salt, {
      N: Number(kp.n),
      r: Number(kp.r),
      p: Number(kp.p),
      dkLen: dklen,
    });
  } else {
    throw new Error(`Unsupported KDF: ${kdf || "none"}`);
  }

  const ct = fromHex(String(c.ciphertext ?? ""));
  const cipherparams = (c.cipherparams ?? {}) as Record<string, unknown>;
  const iv = fromHex(String(cipherparams.iv ?? ""));
  const aesKey = await crypto.subtle.importKey(
    "raw",
    dk.slice(0, 16) as BufferSource,
    { name: "AES-CTR" },
    false,
    ["decrypt"],
  );
  const pt = new Uint8Array(
    await crypto.subtle.decrypt(
      { name: "AES-CTR", counter: iv as BufferSource, length: 128 },
      aesKey,
      ct as BufferSource,
    ),
  );
  const phrase = dec.decode(pt).trim().replace(/\s+/g, " ").toLowerCase();
  if (!isValidMnemonic(phrase)) throw new Error("Wrong password or unsupported keystore.");
  return phrase;
};

/** True if a parsed JSON object looks like our Shieldz (mnemonic) keystore. */
const isShieldzKeystore = (o: Record<string, unknown>): boolean =>
  o.kdf === "PBKDF2-SHA256" && typeof o.ct_b64 === "string" && typeof o.salt_b64 === "string";

/** True if a parsed JSON object looks like a standard Web3 V3 keystore. */
const isV3Keystore = (o: Record<string, unknown>): boolean =>
  Number(o.version) === 3 && (o.crypto != null || o.Crypto != null);

/** True if a parsed JSON object looks like an XChainJS keystore (version 1,
 *  nested crypto, or the "xchain-keystore" meta tag). */
const isXchainKeystore = (o: Record<string, unknown>): boolean =>
  (o.crypto != null || o.Crypto != null) &&
  (Number(o.version) === 1 || String(o.meta ?? "").toLowerCase().includes("xchain"));

/**
 * Resolve ANY supported login secret into a signing-capable viem account
 * (address + signMessage), all in-browser:
 *   - Shieldz keystore JSON (wraps the 12-word mnemonic)   → needs passphrase
 *   - XChainJS keystore JSON (THORSwap/ASGARDEX/XDEFI/…)   → needs password
 *   - Standard Web3 V3 keystore JSON (single EVM key)      → needs password
 *   - A raw BIP-39 mnemonic phrase                         → no passphrase
 *
 * The returned account signs at m/44'/60'/0'/0/0 for keystores/mnemonics and
 * at the embedded key for V3 files. Throws a human-readable Error on bad input
 * or wrong password. Callers should not retain the input after this resolves.
 */
export const loginAccountFromSecret = async (input: {
  /** Parsed keystore JSON (Shieldz, XChainJS, or V3). Omit when using a mnemonic. */
  keystore?: Record<string, unknown>;
  /** Raw BIP-39 mnemonic phrase. Omit when using a keystore. */
  mnemonic?: string;
  /** Decryption secret for a keystore (ignored for a mnemonic). */
  passphrase?: string;
}): Promise<LocalAccount> => {
  if (input.mnemonic != null && input.mnemonic.trim().length > 0) {
    const phrase = input.mnemonic.trim().replace(/\s+/g, " ").toLowerCase();
    if (!isValidMnemonic(phrase)) throw new Error("That doesn't look like a valid seed phrase.");
    return mnemonicToAccount(phrase);
  }
  const ks = input.keystore;
  if (!ks) throw new Error("Provide a keystore file or a seed phrase.");
  const passphrase = input.passphrase ?? "";
  if (isShieldzKeystore(ks)) {
    const mnemonic = await importKeystore(ks as unknown as Keystore, passphrase);
    return mnemonicToAccount(mnemonic);
  }
  // XChainJS is checked before V3: both nest a `crypto` block, but XChainJS is
  // version 1 and decrypts to a mnemonic, while V3 is version 3 and decrypts to
  // a raw key — the version discriminates and isXchainKeystore requires v1.
  if (isXchainKeystore(ks)) {
    const mnemonic = await importXchainKeystore(ks, passphrase);
    return mnemonicToAccount(mnemonic);
  }
  if (isV3Keystore(ks)) {
    const priv = await importV3Keystore(ks, passphrase);
    return privateKeyToAccount(`0x${toHex(priv)}`);
  }
  throw new Error("Unrecognized keystore format.");
};

/** Trigger a browser download of a Keystore as JSON. */
export const downloadKeystore = (blob: Keystore, filename: string): void => {
  const data = new Blob([JSON.stringify(blob, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
